import type { BlogPost } from '../data/blog';
import { SITE } from '../data/site';

export const SEO = {
  siteName: SITE.name,
  author: 'Hello Gwiji Financial Services',
  locale: 'en_KE',
  themeColor: '#1e380f',
  twitterSite: '@HelloGwiji',
  defaultImage: `${SITE.url}/images/Hello%20Gwiji%20Bridge.png`,
  defaultImageAlt: 'Hello Gwiji transparent capital partnerships platform',
  keywords:
    'fintech Kenya, capital partnerships, venture capital, alternative investments, business funding, investor platform, Hello Gwiji, transparent investing, entrepreneur funding',
  sameAs: [
    'https://www.linkedin.com/company/hello-gwiji/',
    'https://www.instagram.com/hello_gwiji/',
  ],
} as const;

export type PageSeoType = 'website' | 'article';

export type PageSeoOptions = {
  title: string;
  description: string;
  path?: string;
  image?: string;
  imageAlt?: string;
  type?: PageSeoType;
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  authorName?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

const MANAGED_SELECTOR = '[data-seo-managed]';
const JSON_LD_ID = 'page-json-ld';

export function absoluteUrl(path = '/'): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE.url}${normalized}`;
}

function upsertMeta(
  attribute: 'name' | 'property',
  key: string,
  content: string,
): HTMLMetaElement {
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`,
  );

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    element.dataset.seoManaged = 'true';
    document.head.appendChild(element);
  }

  element.content = content;
  return element;
}

function upsertLink(rel: string, href: string): HTMLLinkElement {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    element.dataset.seoManaged = 'true';
    document.head.appendChild(element);
  }

  element.href = href;
  return element;
}

function setJsonLd(data: Record<string, unknown> | Record<string, unknown>[]): void {
  let script = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement('script');
    script.id = JSON_LD_ID;
    script.type = 'application/ld+json';
    script.dataset.seoManaged = 'true';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

function removeJsonLd(): void {
  document.getElementById(JSON_LD_ID)?.remove();
}

export function applyPageSeo(options: PageSeoOptions): () => void {
  const {
    title,
    description,
    path = '/',
    image = SEO.defaultImage,
    imageAlt = SEO.defaultImageAlt,
    type = 'website',
    noindex = false,
    publishedTime,
    modifiedTime,
    authorName,
    jsonLd,
  } = options;

  const canonical = absoluteUrl(path);
  const robots = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';

  document.title = title;

  upsertMeta('name', 'description', description);
  upsertMeta('name', 'keywords', SEO.keywords);
  upsertMeta('name', 'author', SEO.author);
  upsertMeta('name', 'robots', robots);
  upsertMeta('name', 'googlebot', noindex ? 'noindex, nofollow' : 'index, follow');
  upsertMeta('name', 'theme-color', SEO.themeColor);

  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:type', type);
  upsertMeta('property', 'og:site_name', SEO.siteName);
  upsertMeta('property', 'og:locale', SEO.locale);
  upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:image:alt', imageAlt);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:site', SEO.twitterSite);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', image);
  upsertMeta('name', 'twitter:image:alt', imageAlt);

  if (publishedTime) {
    upsertMeta('property', 'article:published_time', publishedTime);
  }
  if (modifiedTime) {
    upsertMeta('property', 'article:modified_time', modifiedTime);
  }
  if (authorName) {
    upsertMeta('property', 'article:author', authorName);
  }

  upsertLink('canonical', canonical);

  if (jsonLd) {
    setJsonLd(jsonLd);
  } else {
    removeJsonLd();
  }

  return () => {
    document.head.querySelectorAll(MANAGED_SELECTOR).forEach((node) => node.remove());
    removeJsonLd();
  };
}

export function organizationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    name: SITE.name,
    url: `${SITE.url}/`,
    logo: `${SITE.url}/logo.png`,
    image: SEO.defaultImage,
    description: SITE.description,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Nairobi',
      addressCountry: 'KE',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: SITE.supportEmail,
      contactType: 'customer service',
    },
    sameAs: SEO.sameAs,
  };
}

export function websiteJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE.name,
    url: `${SITE.url}/`,
    description: SITE.description,
    publisher: {
      '@type': 'Organization',
      name: SITE.name,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE.url}/logo.png`,
      },
    },
  };
}

export function blogPostingJsonLd(post: BlogPost): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Person',
      name: post.author.name,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE.name,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE.url}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': absoluteUrl(`/blog/${post.slug}`),
    },
  };
}

export function blogIndexJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: `${SITE.name} Blog`,
    description:
      'Insights on transparent capital partnerships, venture funding, and building trust between investors and entrepreneurs.',
    url: absoluteUrl('/blog'),
    publisher: {
      '@type': 'Organization',
      name: SITE.name,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE.url}/logo.png`,
      },
    },
  };
}

export const PAGE_SEO = {
  home: {
    title: `${SITE.name}, ${SITE.tagline}`,
    description: SITE.description,
    path: '/',
  },
  application: {
    title: `${SITE.name}, Application`,
    description:
      'Explore the Hello Gwiji application, discover ventures, connect with partners, agree on clear terms, and grow together through our two-layer platform.',
    path: '/application',
  },
  blog: {
    title: `${SITE.name}, Blog`,
    description:
      'Insights on transparent capital partnerships, due diligence, venture funding, and building trust between investors and entrepreneurs in East Africa.',
    path: '/blog',
  },
  contact: {
    title: `${SITE.name}, Contact`,
    description:
      'Get in touch with the Hello Gwiji team. Email support@hellogwiji.com or schedule a call to learn how transparent capital partnerships work.',
    path: '/contact',
  },
  waitlist: {
    title: `${SITE.name}, Join the Waitlist`,
    description:
      'Join the Hello Gwiji waitlist for early access. Connect with verified founders, move money securely, and pay where life happens.',
    path: '/waitlist',
  },
  privacy: {
    title: `${SITE.name}, Privacy Policy`,
    description:
      'Read the Hello Gwiji privacy policy, how we collect, use, and protect your personal information on our capital partnerships platform.',
    path: '/privacy-policy',
  },
  terms: {
    title: `${SITE.name}, Terms and Conditions`,
    description:
      'Review the Hello Gwiji terms and conditions governing use of our platform for investors, entrepreneurs, and business partners.',
    path: '/terms-and-conditions',
  },
  ageSuitability: {
    title: `${SITE.name}, Age Suitability`,
    description:
      'Learn about age requirements and eligibility to use the Hello Gwiji capital partnerships platform.',
    path: '/age-suitability',
  },
  notFound: {
    title: `${SITE.name}, Page not found`,
    description: 'The page you are looking for could not be found on Hello Gwiji.',
    path: '/404',
    noindex: true as const,
  },
} as const;
